import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, map, Observable, of, ReplaySubject, Subject, Subscription } from 'rxjs';
import { completeSubject, promiseWithCatch } from 'src/app/common/app.helpers';
import ProjectLinks from 'src/app/common/project.links';
import { FormTypes } from 'src/app/forms/forms.enum';
import { TableCellOnActionEvent } from 'src/app/interfaces/tables.interfaces';
import {
  AccountInvolvements,
  CommunitiesBasic,
  CommunityCounters,
  CommunityInviteOrDeclineRequest,
  CreateCommunityRequestBasic,
  EmptyResponse,
  PaginatableListOfBasicUsers,
  RequestCountersBasic,
  UpdateCommunityRequestBasic,
  UserBasic,
} from 'src/app/swagger-api';
import { UserTableCreators } from 'src/app/views/table-users-list/adapters/table-creator.enum';
import { TableCreatorFactory } from 'src/app/views/table-users-list/adapters/table-creator.method';
import { withReplaceBadRequest } from '../../services/api-error';

import { loadingHttpContext } from 'src/app/common/interceptors/tokens';
import {
  SocialGroupMembers,
  SocialGroupType,
} from '../search/search-component/invite-to-social-group/social-group.interface';
import { SocialInitiative } from '../shared/social-initiative/social-initiative.abstract';
import { CommunityStore, CommunityView } from './communities-store';
import { COMMUNITY_FORM_FIELDS_ATTRIBUTES } from './community.helpers';

@Injectable({ providedIn: 'root' })
export class ManageCommunityService<
  TCreateRequest extends CreateCommunityRequestBasic,
  TUpdateRequest extends UpdateCommunityRequestBasic,
> extends SocialInitiative<CommunityView> {
  private communityChanges$: Subject<CommunityView> = new ReplaySubject<CommunityView>(1);
  private communityTabsCountersChanges$: Subject<CommunityCounters> = new Subject();
  private communityInvolvementCountersChanges$: Subject<RequestCountersBasic> = new Subject();

  private communityStore = inject(CommunityStore);

  public readonly SOCIAL_GROUP_TYPE: SocialGroupType = 'Community';
  public readonly SOCIAL_ENTITY_ROUTE_LINK: string = this.communityStore.VIEW_ROUTE;
  public readonly SOCIAL_ENTITY_MANAGE_ROUTE_LINK: string = this.communityStore.MANAGE_ROUTE;
  public readonly SOCIAL_ENTITY_INVITE_ROUTE_LINK: string = this.communityStore.INVITE_ROUTE;

  public get communityInvolvementCountersChanges(): Observable<RequestCountersBasic> {
    return this.communityInvolvementCountersChanges$.asObservable();
  }

  public get entityChanged$(): Observable<CommunityView> {
    return this.communityChanges$.asObservable();
  }

  public get communityTabsCountersChanges(): Observable<CommunityCounters> {
    return this.communityTabsCountersChanges$.asObservable();
  }

  public fetchRequestCommunityCounters(communityId: string): void {
    const subscription: Subscription = this.apiService
      .restSocialCommunitiesCounters(communityId)
      .pipe(catchError(() => EMPTY))
      .subscribe((counters: CommunityCounters) => {
        this.communityTabsCountersChanges$.next(counters);
        subscription.unsubscribe();
      });
  }

  public fetchRequestCounters(communityId: string): void {
    const subscription: Subscription = this.apiService
      .restSocialCommunityInvolvementCountersGet(communityId)
      .pipe(catchError(() => EMPTY))
      .subscribe((counters: RequestCountersBasic) => {
        this.communityInvolvementCountersChanges$.next(counters);
        subscription.unsubscribe();
      });
  }

  public fetchEntityMemberIds(socialGroupId: string): Observable<SocialGroupMembers> {
    return this.apiService.restSocialCommunitiesAccountInvolvements(socialGroupId).pipe(
      map((involvements: AccountInvolvements): SocialGroupMembers => {
        return {
          socialGroupId: involvements.social_group_id,
          memberIds: new Set(involvements.participant_account_ids),
          pendingMemberIds: new Set(involvements.pending_invitation_account_ids),
          pendingJoinRequests: new Set(involvements.pending_request_account_ids),
        };
      }),
      // On error, just return empty sets
      catchError(() => {
        return of({
          socialGroupId: socialGroupId,
          memberIds: new Set<string>(),
          pendingMemberIds: new Set<string>(),
          pendingJoinRequests: new Set<string>(),
        });
      }),
    );
  }

  public fetchEntityMembers(communityId: string, offset?: number, limit?: number): void {
    this.apiService
      .restSocialCommunityMembersIndex(communityId, offset, limit, loadingHttpContext())
      .pipe(catchError(() => EMPTY))
      .subscribe((paginatableUsers: PaginatableListOfBasicUsers) => {
        this.members = paginatableUsers.items;
        this.membersChanges$.next(paginatableUsers);

        const membersForTable: UserBasic[] = paginatableUsers.items.filter(
          (user: UserBasic) => user.user_id !== this.entity?.creator.user_id,
        );

        this.membersTableDataChanges$.next(
          TableCreatorFactory.createTableConstructorData(UserTableCreators.MANAGE_USERS, {
            users: membersForTable,
            entity: this.entity,
          }),
        );
      });
  }

  public fetchAttributeMetadata(data: Partial<CommunityView> | object = {}): void {
    this.formAttrChanges$.next({
      data,
      formAttribute: this.formsAttributeService.getFormAttribute(FormTypes.COMMUNITY),
      fieldAttributes: this.fieldsService
        .convertAttributesMetadata(COMMUNITY_FORM_FIELDS_ATTRIBUTES, FormTypes.COMMUNITY)
        .map((item) => {
          if (item.backendAttribute.name === 'focus') {
            item.disabled = !!(data as CommunityView).entity_id;
          }

          return item;
        }),
    });
  }

  public fetchUsersRequestsForEntity(
    entityId: string,
    offset: number,
    limit: number,
    sorting: string,
    allowManaging: boolean,
  ): void {
    this.fetchEntityUsersRequests(
      this.apiService.restSocialCommunityRequestsIndex(entityId, offset, limit, sorting, loadingHttpContext()),
      entityId,
      allowManaging,
    );
  }

  public fetchUsersInvitationsForEntity(
    entityId: string,
    offset: number,
    limit: number,
    sorting: string,
    allowManaging: boolean,
  ): void {
    this.fetchEntityInvites(
      this.apiService.restSocialCommunityInvitesIndex(entityId, offset, limit, sorting, loadingHttpContext()),
      entityId,
      allowManaging,
    );
  }

  public fetchEntityDetails(entityId: string): void {
    this.communityStore.get(entityId).subscribe({
      next: (community: CommunityView) => {
        this.entity = community;
        this.communityChanges$.next(community);
      },
      error: () => {
        promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_DEFAULT));
      },
    });
  }

  public createEntity(entity: TCreateRequest): void {
    this.communityStore
      .create(entity)
      .pipe(catchError(() => EMPTY))
      .subscribe((community: CommunityView) => {
        promiseWithCatch(this.router.navigateByUrl(`${this.SOCIAL_ENTITY_ROUTE_LINK}/${community.entity_id}`));
      });
  }

  public updateEntity(entity: TUpdateRequest, entityId: string): void {
    this.communityStore
      .update(entity, entityId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        promiseWithCatch(this.router.navigateByUrl(`${this.SOCIAL_ENTITY_ROUTE_LINK}/${entityId}`));
      });
  }

  public deleteCommunity(communityId: string): void {
    this.communityStore
      .delete(communityId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        promiseWithCatch(
          this.router.navigateByUrl(
            this.sessionService.isAdminSession() ? ProjectLinks.LINK_DEFAULT : ProjectLinks.LINK_COMMUNITY_ALL,
          ),
        );
      });
  }

  public removeMember(userId: string, entityId: string, offset: number, limit: number): void {
    this.apiService
      .restSocialCommunityMembersDelete(entityId, userId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.fetchEntityData(entityId);
        this.fetchEntityMembers(entityId, offset, limit);
      });
  }

  public leaveEntity(entityId: string, offset: number, limit: number): void {
    this.apiService
      .restSocialCommunityMembersDelete(entityId, this.sessionService.userId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.fetchEntityData(entityId);
        this.fetchEntityMembers(entityId, offset, limit);
      });
  }

  public joinEntity(entityId: string, offset: number, limit: number): void {
    this.apiService
      .restSocialCommunityMembersPost({ user_id: this.sessionService.userId }, entityId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.fetchEntityData(entityId);
        this.fetchEntityMembers(entityId, offset, limit);
      });
  }

  public inviteToEntity(userId: string, entityId: string): void {
    this.apiService
      .restSocialCommunityMembersPost({ user_id: userId }, entityId)
      .pipe(catchError(() => EMPTY))
      .subscribe(() => {
        this.fetchEntityData(entityId);
      });
  }

  public navigateToInviteToEntitySearchPage(id: string): void {
    this.navigateToInvite(`${this.SOCIAL_ENTITY_INVITE_ROUTE_LINK}/${id}`);
  }

  public getRelatedCommunities(id: string, limit?: number, offset?: number): Observable<CommunitiesBasic> {
    return this.apiService.restSocialRelatedCommunitiesIndex(id, offset, limit);
  }

  public onConfirmRequestOrInvite(event: TableCellOnActionEvent): Observable<EmptyResponse> {
    return this.apiService
      .restSocialCommunityMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Approved },
        event.entityId,
        event.actorId,
      )
      .pipe(withReplaceBadRequest({}));
  }

  public onRemoveInvite(event: TableCellOnActionEvent): Observable<EmptyResponse> {
    return this.apiService
      .restSocialCommunityMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY));
  }

  public onRejectRequestOrInvite(event: TableCellOnActionEvent): Observable<EmptyResponse> {
    return this.apiService
      .restSocialCommunityMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        event.entityId,
        event.actorId,
      )
      .pipe(catchError(() => EMPTY));
  }

  protected override completeSubject(): void {
    super.completeSubject();

    completeSubject(this.communityChanges$);
  }
}
